import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useContext, useMemo} from 'react';

import {trackCreateReportStarted} from '../../../services/analytics';
import {isInJupyterNotebook} from '../../../setup';
import {useRampFlagCoreweaveIssues} from '../../../state/issues/useCoreweaveIssues';
import {PanelBankUpdaterContext} from '../../../state/panelbank/context';
import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {usePanelBankConfig} from '../../../state/views/panelBankConfig/hooks';
import type * as PanelBankConfigTypes from '../../../state/views/panelBankConfig/types';
import {getSectionsWithVisiblePanels} from '../../../util/panelbank';
import {type LayedOutPanel} from '../../../util/panelTypes';
import {usePanelIsActive} from '../../../util/usePanelIsActive';
import {CreateReportModal} from '../../CreateReportModal';
import {useWorkspaceSettingsForReportCreation} from '../../CreateReportModal/useReportSettings';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {WorkspaceSettings} from '../../SettingControls/WorkspaceSettings';
import {ToggleMinimizeRunSelectorButton} from '../../ToggleMinimizeRunSelectorButton';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {RunIssuesButton} from '../../WorkspaceDrawer/RunIssues/RunIssuesButton';
import {usePanelBankWidthContext} from '../contextProviders/PanelBankWidthContext';
import PanelSearchHideControl from '../search/PanelSearchHideControl';
import {PanelSearchInput} from '../search/PanelSearchInput';
import {SearchAutoExpandToggle} from '../search/SearchAutoExpandToggle';
import {SearchResultCounts} from '../search/SearchResultCounts';
import {usePanelSearchUpdatePending} from '../search/useIsPanelSearchUpdatePending';
import {type PanelBankSectionConfig} from '../types';
import {AddPanelsDrawerButton} from './AddPanelsDrawerButton';
import {PanelBankControlsOverflowMenu} from './PanelBankControlsOverflowMenu';
import {PanelBankCreateReportButton} from './PanelBankCreateReportButton';

const PANEL_BANK_CONTROLS_ICON_ONLY_WIDTH = 960;

interface PanelBankControlsProps {
  entityName: string;
  projectName: string;
  isProjectReadOnly?: boolean;
  panelBankConfigRef: PanelBankConfigTypes.Ref;
  workspaceID?: string;
  isMobileWorkspaceTab?: boolean;
  isRunSelectorMinimized?: boolean;
  toggleMinimizeRunSelectorWithTracking?: (method: 'hotkey' | 'button') => void;
}

// This is the bar at the top of the PanelBank that contains search and settings
export const PanelBankControls = (props: PanelBankControlsProps) => {
  const {
    entityName,
    panelBankConfigRef,
    projectName,
    isProjectReadOnly,
    workspaceID,
    isMobileWorkspaceTab,
    isRunSelectorMinimized,
    toggleMinimizeRunSelectorWithTracking,
  } = props;

  const {isSingleRun} = useSingleRunModeContext();

  const isIssuesObservabilityEnabled = useRampFlagCoreweaveIssues();

  const {setExportingUnknownID} = useContext(PanelBankUpdaterContext);
  const setExporting = useCallback(
    () => setExportingUnknownID(true),
    [setExportingUnknownID]
  );

  const {
    debouncedHideSearchRegex: hideSearchRegex,
    debouncedSearchRegex: searchRegex,
    isHiding,
    isSearching,
  } = usePanelSearchDebouncedContext();
  const hasPanelSearchFilter = isSearching || isHiding;
  const isPanelSearchUpdatePending = usePanelSearchUpdatePending();

  const {panelBankWidth} = usePanelBankWidthContext();
  const useExportableSections = () => {
    const {sections} = usePanelBankConfig(panelBankConfigRef);
    return useMemo(() => getSectionsWithVisiblePanels(sections), [sections]);
  };

  /**
   * Note(jofang): Purposefully leaving this commented out code in while I
   * wait for product and design to decide on the direction of this use case.
   */
  // const pushDraftReport = usePushReport(entityName, projectName);

  const panelIsActive = usePanelIsActive();
  const panelIsActiveWithSearch = useCallback(
    (section: PanelBankSectionConfig, panel: LayedOutPanel) =>
      panelIsActive({section, panel, searchRegex, hideSearchRegex}),
    [hideSearchRegex, panelIsActive, searchRegex]
  );

  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const inheritedSettings =
    useWorkspaceSettingsForReportCreation(workspaceSettingsRef);

  const shouldDisplayIconOnly =
    panelBankWidth <= PANEL_BANK_CONTROLS_ICON_ONLY_WIDTH;

  const onToggleMinimizeRunSelector = useCallback(() => {
    toggleMinimizeRunSelectorWithTracking?.('button');
  }, [toggleMinimizeRunSelectorWithTracking]);

  const onCreateReport = useCallback(() => {
    trackCreateReportStarted({
      location: `workspace create report button`,
      entityName,
      projectName,
    });

    setExporting();
  }, [setExporting, entityName, projectName]);

  return (
    <TailwindContents>
      <div
        data-test="panel-bank-controls"
        className="p-16 border-b border-b-wb-border block flex-1 min-w-0 w-full">
        <div className="flex gap-x-8 gap-y-16 w-full">
          {isRunSelectorMinimized &&
            !isSingleRun &&
            !isMobileWorkspaceTab &&
            toggleMinimizeRunSelectorWithTracking && (
              <ToggleMinimizeRunSelectorButton
                variant="secondary"
                toMinimize={false}
                onToggle={onToggleMinimizeRunSelector}
              />
            )}
          <div className="min-w-0 flex-1">
            <PanelSearchInput />
          </div>
          <div className="shrink-0">
            <PanelSearchHideControl />
          </div>
          <div className="hidden gap-x-8 md:flex">
            <PanelBankControlsOverflowMenu />
            {workspaceID != null && (
              <WorkspaceSettings isIconOnly={shouldDisplayIconOnly} />
            )}
            <div className="flex gap-8">
              {!isProjectReadOnly && (
                <>
                  <div>
                    {!isInJupyterNotebook() && (
                      <PanelBankCreateReportButton
                        isIconOnly={shouldDisplayIconOnly}
                        disabled={false}
                        entityName={entityName}
                        projectName={projectName}
                        onClick={onCreateReport}
                      />
                    )}
                  </div>
                  {isIssuesObservabilityEnabled && isSingleRun && (
                    <RunIssuesButton />
                  )}
                  <CreateReportModal
                    inheritedSettings={inheritedSettings}
                    useExportableSections={useExportableSections}
                    panelIsActive={panelIsActiveWithSearch}
                  />
                </>
              )}

              <AddPanelsDrawerButton />
            </div>
          </div>
        </div>
        {(isPanelSearchUpdatePending || hasPanelSearchFilter) && (
          <div className="mt-8 flex items-start gap-16">
            <SearchResultCounts />
            <SearchAutoExpandToggle />
          </div>
        )}
      </div>
    </TailwindContents>
  );
};
